import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import {
    ArrowLeft,
    Bell,
    Copy,
    Edit,
    Key,
    Loader2,
    Mail,
    Phone,
    Shield,
    Trash2,
    User,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useSWRConfig } from 'swr';

import { UserFormModal } from '@apps/web/components/admin/user_form_modal';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { Card } from '@apps/web/components/ui/card';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { MultiSelect } from '@apps/web/components/ui/multi_select';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { useAdminMode } from '@apps/web/lib/admin_mode_store';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { type NotificationTypeKey } from '@apps/web/lib/notification_catalog.generated';
import { isNotificationChannelSupportedForType } from '@apps/web/lib/notification_channel_preferences';
import {
    getNotificationSettingsGroupCheckedState,
    getVisibleNotificationSettingsSections,
    isPlanOfCareDutyMonitoringEnabled,
} from '@apps/web/lib/notification_settings_catalog';
import {
    useAdminUser,
    useAdminUserNotificationPreferences,
    useAdminUsers,
    useOrganization,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

const INTERNAL_TAG_OPTIONS = [
    { value: 'decision_maker', label: 'Decision Maker' },
    { value: 'champion', label: 'Champion' },
    { value: 'training_lead', label: 'Training Lead' },
    { value: 'core_scheduler', label: 'Core Scheduler' },
] as const;

type InternalUserTag = (typeof INTERNAL_TAG_OPTIONS)[number]['value'];

const INTERNAL_TAG_LABELS: Record<InternalUserTag, string> = {
    decision_maker: 'Decision Maker',
    champion: 'Champion',
    training_lead: 'Training Lead',
    core_scheduler: 'Core Scheduler',
};

function isInternalUserTag(value: string): value is InternalUserTag {
    return INTERNAL_TAG_OPTIONS.some((option) => option.value === value);
}

function toInternalUserTags(values: Array<string>): Array<InternalUserTag> {
    return values.filter(isInternalUserTag);
}

export const Route = createFileRoute('/_app/admin/users/$user-id')({
    head: () => ({
        meta: [
            {
                title: 'User Details | Phoebe Admin',
            },
        ],
    }),
    component: UserDetailPage,
});

function UserDetailPage() {
    const { 'user-id': userId } = Route.useParams();

    return (
        <div className="h-full overflow-x-hidden overflow-y-auto">
            <div className="max-w-5xl p-6">
                <div className="mb-6">
                    <Link
                        to="/admin/users"
                        className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2 text-sm"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to Users
                    </Link>
                </div>
                <UserDetails userId={userId} />
            </div>
        </div>
    );
}

function UserDetails({ userId }: { userId: string }) {
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [editingOrganizationId, setEditingOrganizationId] = useState<
        string | null
    >(null);
    const [editingInternalTags, setEditingInternalTags] = useState<
        Array<InternalUserTag>
    >([]);
    const [isSavingInternalTags, setIsSavingInternalTags] = useState(false);
    const [isRemovingOrganization, setIsRemovingOrganization] = useState(false);
    const [isResettingPassword, setIsResettingPassword] = useState(false);
    const [recoveryLink, setRecoveryLink] = useState<string | null>(null);
    const [recoveryCode, setRecoveryCode] = useState<string | null>(null);
    const [showRecoveryDialog, setShowRecoveryDialog] = useState(false);
    const router = useRouter();
    const { mutate } = useSWRConfig();
    const { mutate: mutateUsers } = useAdminUsers();

    const revalidateAllUsers = () =>
        mutate(
            (key) => typeof key === 'string' && key.includes('/admin/users'),
        );

    const { data: user, isLoading, mutate: mutateUser } = useAdminUser(userId);

    const handleResetPassword = async () => {
        if (!userId || !user) {
            return;
        }

        const confirmed = window.confirm(
            `Are you sure you want to generate a password reset link for ${user.email}?`,
        );

        if (!confirmed) {
            return;
        }

        setIsResettingPassword(true);

        try {
            const response = await getApiClient().post({
                url: '/admin/users/{user_id}/reset-password',
                pathParams: { user_id: userId },
            });

            setRecoveryLink(response.recovery_link);
            setRecoveryCode(response.recovery_code || null);
            setShowRecoveryDialog(true);
            toast.success('Password reset link generated successfully', {
                persistNotification: false,
            });
        } catch (error) {
            toast.error('Failed to generate password reset link', {
                persistNotification: false,
            });
        } finally {
            setIsResettingPassword(false);
        }
    };

    const handleCopyLink = async () => {
        if (recoveryLink) {
            await navigator.clipboard.writeText(recoveryLink);
            toast.success('Link copied to clipboard', {
                persistNotification: true,
                notificationType: 'app_feedback',
            });
        }
    };

    const handleOpenInternalTagsDialog = (
        organizationId: string,
        internalTags: Array<string>,
    ) => {
        setEditingOrganizationId(organizationId);
        setEditingInternalTags(toInternalUserTags(internalTags));
    };

    const resetInternalTagsDialog = () => {
        setEditingOrganizationId(null);
        setEditingInternalTags([]);
    };

    const handleCloseInternalTagsDialog = () => {
        if (isSavingInternalTags) {
            return;
        }

        resetInternalTagsDialog();
    };

    const handleSaveInternalTags = async () => {
        if (!userId || !editingOrganizationId) {
            return;
        }

        setIsSavingInternalTags(true);

        try {
            await getApiClient().put({
                url: '/admin/users/{user_id}/organizations/{organization_id}/internal-tags',
                pathParams: {
                    user_id: userId,
                    organization_id: editingOrganizationId,
                },
                body: {
                    internal_tags: editingInternalTags,
                },
            });

            toast.success('Internal tags updated successfully', {
                persistNotification: false,
            });
            resetInternalTagsDialog();
            await mutateUser();
            await revalidateAllUsers();
        } catch (error) {
            toast.error('Failed to update internal tags', {
                persistNotification: false,
            });
        } finally {
            setIsSavingInternalTags(false);
        }
    };

    const handleRemoveOrganization = async (organizationId: string) => {
        if (!userId || !user) {
            return;
        }

        const org = (user.organizations ?? []).find(
            (organization) => organization.id === organizationId,
        );
        const organizationDisplayName = org?.internal_name ?? org?.name;
        const confirmed = window.confirm(
            `Are you sure you want to remove ${user.email} from ${organizationDisplayName}?`,
        );

        if (!confirmed) {
            return;
        }

        setIsRemovingOrganization(true);

        try {
            await getApiClient().delete({
                url: '/admin/users/{user_id}/organizations/{organization_id}',
                pathParams: {
                    user_id: userId,
                    organization_id: organizationId,
                },
            });

            toast.success('Organization removed successfully', {
                persistNotification: false,
            });

            await mutateUser();
            await revalidateAllUsers();
        } catch (error) {
            toast.error('Failed to remove organization', {
                persistNotification: false,
            });
        } finally {
            setIsRemovingOrganization(false);
        }
    };

    const handleDelete = async () => {
        if (!userId || !user) {
            return;
        }

        const confirmed = window.confirm(
            `Are you sure you want to delete ${user.email}? This action cannot be undone.`,
        );

        if (!confirmed) {
            return;
        }

        setIsDeleting(true);

        try {
            await getApiClient().delete({
                url: '/admin/users/{user_id}',
                pathParams: { user_id: userId },
            });

            toast.success('User deleted successfully', {
                persistNotification: false,
            });
            revalidateAllUsers();
            router.navigate({ to: '/admin/users' });
        } catch (error) {
            toast.error('Failed to delete user', {
                persistNotification: false,
            });
            setIsDeleting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex h-full items-center justify-center">
                <div className="text-muted-foreground flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Loading user details…</span>
                </div>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="text-muted-foreground flex h-full items-center justify-center">
                User not found
            </div>
        );
    }

    const displayName =
        user.first_name || user.last_name
            ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
            : user.email || 'Unnamed User';
    const organizations = user.organizations ?? [];
    const editingOrganization =
        organizations.find((org) => org.id === editingOrganizationId) ?? null;

    return (
        <div className="max-w-full min-w-0 space-y-6 overflow-hidden">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <User className="text-muted-foreground/70 h-6 w-6" />
                    <h1 className="text-2xl font-semibold">{displayName}</h1>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsEditModalOpen(true)}
                    >
                        <Edit className="mr-2 h-4 w-4" /> Edit
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleResetPassword}
                        disabled={isResettingPassword}
                    >
                        <Key className="mr-2 h-4 w-4" />
                        {isResettingPassword
                            ? 'Generating...'
                            : 'Reset Password'}
                    </Button>
                    <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleDelete}
                        disabled={isDeleting}
                    >
                        <Trash2 className="mr-2 h-4 w-4" />
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </div>
            </div>
            <div className="flex items-center gap-2">
                {user.admin && (
                    <Badge variant="secondary" className="gap-1">
                        <Shield className="h-3 w-3" />
                        Admin
                    </Badge>
                )}
                {user.phoebe_role && (
                    <Badge variant="outline" className="gap-1">
                        {user.phoebe_role}
                    </Badge>
                )}
                {user.persona && (
                    <Badge variant="outline" className="gap-1">
                        {user.persona.replace(/_/g, ' ')}
                    </Badge>
                )}
                {user.is_gated && (
                    <Badge variant="outline">Mobile app gated</Badge>
                )}
            </div>
            <Card className="p-6">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <h3 className="text-muted-foreground text-sm font-medium">
                            User ID
                        </h3>
                        <p className="mt-1 font-mono text-sm">{user.id}</p>
                    </div>
                    <div>
                        <h3 className="text-muted-foreground text-sm font-medium">
                            Email
                        </h3>
                        <p className="mt-1 flex items-center gap-2">
                            {user.email ? (
                                <React.Fragment>
                                    <Mail className="text-muted-foreground/70 h-4 w-4" />
                                    {user.email}
                                </React.Fragment>
                            ) : (
                                <span className="text-muted-foreground">
                                    No email
                                </span>
                            )}
                        </p>
                    </div>
                    <div>
                        <h3 className="text-muted-foreground text-sm font-medium">
                            First Name
                        </h3>
                        <p className="mt-1">
                            {user.first_name || (
                                <span className="text-muted-foreground">
                                    Not provided
                                </span>
                            )}
                        </p>
                    </div>
                    <div>
                        <h3 className="text-muted-foreground text-sm font-medium">
                            Last Name
                        </h3>
                        <p className="mt-1">
                            {user.last_name || (
                                <span className="text-muted-foreground">
                                    Not provided
                                </span>
                            )}
                        </p>
                    </div>
                    <div>
                        <h3 className="text-muted-foreground text-sm font-medium">
                            Phone Number
                        </h3>
                        <p className="mt-1 flex items-center gap-2">
                            {user.phone_number ? (
                                <React.Fragment>
                                    <Phone className="text-muted-foreground/70 h-4 w-4" />
                                    {user.phone_number}
                                </React.Fragment>
                            ) : (
                                <span className="text-muted-foreground">
                                    Not provided
                                </span>
                            )}
                        </p>
                    </div>
                    <div>
                        <h3 className="text-muted-foreground text-sm font-medium">
                            Timezone
                        </h3>
                        <p className="mt-1">
                            {user.timezone || (
                                <span className="text-muted-foreground">
                                    Not set
                                </span>
                            )}
                        </p>
                    </div>
                    {user.created_at && (
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                Created At
                            </h3>
                            <p className="mt-1">
                                {new Date(user.created_at).toLocaleDateString(
                                    'en-US',
                                    {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                    },
                                )}
                            </p>
                        </div>
                    )}
                </div>
            </Card>
            <div>
                <h2 className="mb-4 text-lg font-semibold">Organizations</h2>
                <Card className="p-0">
                    {organizations.length > 0 ? (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Organization</TableHead>
                                    <TableHead>Timezone</TableHead>
                                    <TableHead>Role</TableHead>
                                    <TableHead>Internal Tags</TableHead>
                                    <TableHead className="w-[100px]">
                                        Actions
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {organizations.map((org) => (
                                    <TableRow key={org.id}>
                                        <TableCell className="font-medium">
                                            {org.internal_name ?? org.name}
                                        </TableCell>
                                        <TableCell>{org.timezone}</TableCell>
                                        <TableCell>
                                            {org.role ? (
                                                <Badge
                                                    variant="outline"
                                                    className="capitalize"
                                                >
                                                    {org.role}
                                                </Badge>
                                            ) : (
                                                <span className="text-muted-foreground">
                                                    -
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-start justify-between gap-2">
                                                {org.internal_tags &&
                                                org.internal_tags.length > 0 ? (
                                                    <div className="flex flex-wrap gap-1">
                                                        {org.internal_tags.map(
                                                            (tag) => (
                                                                <Badge
                                                                    key={tag}
                                                                    variant="secondary"
                                                                >
                                                                    {
                                                                        INTERNAL_TAG_LABELS[
                                                                            tag
                                                                        ]
                                                                    }
                                                                </Badge>
                                                            ),
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-muted-foreground">
                                                        -
                                                    </span>
                                                )}
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() =>
                                                        handleOpenInternalTagsDialog(
                                                            org.id,
                                                            org.internal_tags ??
                                                                [],
                                                        )
                                                    }
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() =>
                                                    handleRemoveOrganization(
                                                        org.id,
                                                    )
                                                }
                                                disabled={
                                                    isRemovingOrganization
                                                }
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <div className="text-muted-foreground flex h-32 items-center justify-center">
                            User is not a member of any organizations
                        </div>
                    )}
                </Card>
            </div>
            {organizations.length > 0 && (
                <NotificationManagement
                    userId={userId}
                    organizations={organizations}
                />
            )}
            {user && (
                <UserFormModal
                    isOpen={isEditModalOpen}
                    onClose={() => setIsEditModalOpen(false)}
                    mode="edit"
                    userId={userId}
                    initialData={{
                        email: user.email || '',
                        first_name: user.first_name || '',
                        last_name: user.last_name || '',
                        phone_number: user.phone_number || '',
                        timezone: user.timezone || '',
                        organization_ids: organizations.map((org) => org.id),
                        admin: user.admin,
                        phoebe_role:
                            user.phoebe_role === 'contractor'
                                ? 'contractor'
                                : null,
                        persona: user.persona ?? null,
                        role: organizations[0]?.role ?? null,
                    }}
                    mutateUsers={mutateUsers}
                />
            )}
            <Dialog
                open={editingOrganizationId !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        handleCloseInternalTagsDialog();
                    }
                }}
            >
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Edit Internal Tags</DialogTitle>
                        <DialogDescription>
                            Update onboarding stakeholder tags for{' '}
                            <span className="font-medium">
                                {editingOrganization?.name ?? 'this membership'}
                            </span>
                            .
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <MultiSelect
                            options={INTERNAL_TAG_OPTIONS.map((option) => ({
                                value: option.value,
                                label: option.label,
                            }))}
                            selected={editingInternalTags}
                            onChange={(values) =>
                                setEditingInternalTags(
                                    toInternalUserTags(values),
                                )
                            }
                            placeholder="Select internal tags"
                            disabled={isSavingInternalTags}
                        />
                        <div className="flex justify-end gap-2">
                            <Button
                                variant="outline"
                                onClick={handleCloseInternalTagsDialog}
                                disabled={isSavingInternalTags}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSaveInternalTags}
                                disabled={isSavingInternalTags}
                            >
                                {isSavingInternalTags
                                    ? 'Saving...'
                                    : 'Save tags'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <Dialog
                open={showRecoveryDialog}
                onOpenChange={setShowRecoveryDialog}
            >
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Password Reset Generated</DialogTitle>
                        <DialogDescription>
                            The password reset information below will expire in
                            1 hour. Send the link and code to the user securely.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="mt-4 space-y-4">
                        <div className="space-y-2">
                            <p className="text-sm font-medium">
                                Recovery Link:
                            </p>
                            <div className="bg-muted rounded-md p-3">
                                <p className="font-mono text-sm break-all">
                                    {recoveryLink}
                                </p>
                            </div>
                        </div>
                        {recoveryCode && (
                            <div className="space-y-2">
                                <p className="text-sm font-medium">
                                    Recovery Code:
                                </p>
                                <div className="bg-muted rounded-md p-3">
                                    <p className="text-center font-mono text-lg">
                                        {recoveryCode}
                                    </p>
                                </div>
                            </div>
                        )}
                        <Button
                            onClick={handleCopyLink}
                            className="w-full"
                            size="sm"
                        >
                            <Copy className="mr-2 h-4 w-4" />
                            Copy Link
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

type NotificationChannel = 'email' | 'inapp' | 'sms';

const CHANNELS: ReadonlyArray<{
    key: NotificationChannel;
    label: string;
}> = [
    { key: 'email', label: 'Email' },
    { key: 'inapp', label: 'Web' },
    { key: 'sms', label: 'Text' },
];

type AdminOrganization = {
    readonly id: string;
    readonly name: string;
    readonly internal_name?: string | null;
};

function NotificationManagement({
    userId,
    organizations,
}: {
    userId: string;
    organizations: ReadonlyArray<AdminOrganization>;
}) {
    const [selectedOrgId, setSelectedOrgId] = useState<string>(
        organizations[0]?.id ?? '',
    );
    const adminMode = useAdminMode();

    useEffect(() => {
        setSelectedOrgId(organizations[0]?.id ?? '');
    }, [organizations]);

    const {
        data: preferences,
        isLoading,
        mutate: mutatePreferences,
    } = useAdminUserNotificationPreferences(userId, selectedOrgId || null);
    const { data: selectedOrganization } = useOrganization(
        selectedOrgId || null,
        { reason: 'admin-user-notification-settings' },
        adminMode,
    );
    const notificationSections = getVisibleNotificationSettingsSections({
        planOfCareDutyMonitoringEnabled: isPlanOfCareDutyMonitoringEnabled(
            selectedOrganization?.general_settings,
        ),
    });

    const [savingKey, setSavingKey] = useState<string | null>(null);

    const handleToggle = async (
        keys: ReadonlyArray<NotificationTypeKey>,
        channel: NotificationChannel,
        checked: boolean,
    ) => {
        const savingId = `${keys[0]}.${channel}`;
        setSavingKey(savingId);

        try {
            await getApiClient().patch({
                url: '/admin/users/{user_id}/notification-preferences',
                pathParams: { user_id: userId },
                queryParams: { organization_id: selectedOrgId },
                body: Object.fromEntries(
                    keys.map((key) => [key, { [channel]: checked }]),
                ),
            });
            await mutatePreferences();
        } catch {
            toast.error('Failed to update notification preference', {
                persistNotification: false,
            });
        } finally {
            setSavingKey(null);
        }
    };

    const handleToggleGlobal = async (
        field: 'browser_enabled' | 'sound_enabled',
        checked: boolean,
    ) => {
        setSavingKey(field);

        try {
            await getApiClient().patch({
                url: '/admin/users/{user_id}/notification-preferences',
                pathParams: { user_id: userId },
                queryParams: { organization_id: selectedOrgId },
                body: { [field]: checked },
            });
            await mutatePreferences();
        } catch {
            toast.error('Failed to update notification preference', {
                persistNotification: false,
            });
        } finally {
            setSavingKey(null);
        }
    };

    const selectedOrg = organizations.find((org) => org.id === selectedOrgId);

    return (
        <div>
            <div className="mb-4 flex items-center justify-between">
                <h2 className="flex items-center gap-2 text-lg font-semibold">
                    <Bell className="h-5 w-5" />
                    Notification Settings
                </h2>
                {organizations.length > 1 && (
                    <Select
                        value={selectedOrgId}
                        onValueChange={setSelectedOrgId}
                    >
                        <SelectTrigger className="w-[280px]">
                            <SelectValue placeholder="Select organization" />
                        </SelectTrigger>
                        <SelectContent>
                            {organizations.map((org) => (
                                <SelectItem key={org.id} value={org.id}>
                                    {org.internal_name ?? org.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
            </div>
            {organizations.length === 1 && selectedOrg && (
                <p className="text-muted-foreground mb-3 text-sm">
                    {selectedOrg.internal_name ?? selectedOrg.name}
                </p>
            )}
            <Card className="p-0">
                {isLoading ? (
                    <div className="flex h-32 items-center justify-center">
                        <div className="text-muted-foreground flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>Loading notification settings…</span>
                        </div>
                    </div>
                ) : !preferences ? (
                    <div className="text-muted-foreground flex h-32 items-center justify-center text-sm">
                        No notification data available
                    </div>
                ) : (
                    <div className="divide-y">
                        <div className="grid grid-cols-[1fr_3rem_3rem_3rem] items-center gap-x-4 px-4 py-2">
                            <div />
                            {CHANNELS.map((ch) => (
                                <div
                                    key={ch.key}
                                    className="text-muted-foreground text-center text-xs font-medium"
                                >
                                    {ch.label}
                                </div>
                            ))}
                        </div>
                        {notificationSections.map((section) => (
                            <div key={section.title}>
                                <div className="bg-muted/50 px-4 py-2">
                                    <p className="text-sm font-medium">
                                        {section.title}
                                    </p>
                                </div>
                                <div className="divide-y">
                                    {section.items.map((item) => (
                                        <div
                                            key={item.key}
                                            className="grid grid-cols-[1fr_3rem_3rem_3rem] items-center gap-x-4 px-4 py-2.5"
                                        >
                                            <p
                                                className="text-sm"
                                                title={item.helperText}
                                            >
                                                {item.label}
                                            </p>
                                            {CHANNELS.map((ch) => {
                                                const supported =
                                                    isNotificationChannelSupportedForType(
                                                        item.key,
                                                        ch.key,
                                                    );
                                                const checkedState =
                                                    getNotificationSettingsGroupCheckedState(
                                                        item.keys,
                                                        (key) =>
                                                            preferences[key]?.[
                                                                ch.key
                                                            ] === true,
                                                    );
                                                const checked = supported
                                                    ? checkedState
                                                    : false;
                                                return (
                                                    <div
                                                        key={ch.key}
                                                        className="flex justify-center"
                                                    >
                                                        <Checkbox
                                                            aria-label={`${ch.label} for ${item.label}`}
                                                            checked={checked}
                                                            title={
                                                                checkedState ===
                                                                'indeterminate'
                                                                    ? 'Some grouped notification types are on and some are off'
                                                                    : undefined
                                                            }
                                                            disabled={
                                                                !supported ||
                                                                savingKey ===
                                                                    `${item.key}.${ch.key}`
                                                            }
                                                            onCheckedChange={(
                                                                nextChecked,
                                                            ) =>
                                                                handleToggle(
                                                                    item.keys,
                                                                    ch.key,
                                                                    nextChecked ===
                                                                        true,
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                        <div className="px-4 py-3">
                            <div className="flex items-center justify-between">
                                <p className="text-sm font-medium">
                                    Browser notifications
                                </p>
                                <Checkbox
                                    aria-label="Browser notifications"
                                    checked={
                                        preferences.browser_enabled ?? false
                                    }
                                    disabled={savingKey === 'browser_enabled'}
                                    onCheckedChange={(checked) =>
                                        handleToggleGlobal(
                                            'browser_enabled',
                                            checked === true,
                                        )
                                    }
                                />
                            </div>
                        </div>
                        <div className="px-4 py-3">
                            <div className="flex items-center justify-between">
                                <p className="text-sm font-medium">
                                    Sound notifications
                                </p>
                                <Checkbox
                                    aria-label="Sound notifications"
                                    checked={preferences.sound_enabled ?? false}
                                    disabled={savingKey === 'sound_enabled'}
                                    onCheckedChange={(checked) =>
                                        handleToggleGlobal(
                                            'sound_enabled',
                                            checked === true,
                                        )
                                    }
                                />
                            </div>
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );
}
